import { useEffect, useMemo, useState } from 'react'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { DEFAULT_PLAYER_FILTER } from '../types'
import type { PlayerFilter } from '../types'
import type { ServerFilter } from '../queries'

export function usePlayerSearch(
  budgetRemaining: number,
  // Sort column to land on initially (and on reset). Callers pass 'price' before any
  // gameweek is scored — everyone is on 0 points, so points order is meaningless.
  defaultSortBy: PlayerFilter['sortBy'] = DEFAULT_PLAYER_FILTER.sortBy,
) {
  const defaultFilter = useMemo<PlayerFilter>(
    () => ({ ...DEFAULT_PLAYER_FILTER, sortBy: defaultSortBy }),
    [defaultSortBy],
  )

  const [filter, setFilter]                       = useState<PlayerFilter>(defaultFilter)
  const [openAccordionPlayerId, setOpenAccordion] = useState<string | null>(null)

  const debouncedSearch = useDebounce(filter.search, 300)

  // "Em orçamento" caps the upper bound at the remaining saldo (tracked live), otherwise
  // the manual maxPrice handle applies. min always comes from the manual handle.
  const effectiveMaxPrice = filter.onBudgetOnly ? budgetRemaining : filter.maxPrice

  // Debounce the price bounds so dragging the slider (fires continuously) batches into
  // one refetch on settle, mirroring the search-box debounce.
  const debouncedMinPrice = useDebounce(filter.minPrice, 300)
  const debouncedMaxPrice = useDebounce(effectiveMaxPrice, 300)

  // ServerFilter drives the API query — changes trigger a fresh fetch.
  // competitionId is not a user-controlled field; callers override it before passing to usePlayerList.
  const serverFilter: ServerFilter = useMemo(() => ({
    search:         debouncedSearch,
    position:       filter.position,
    teamCode:       filter.teamCode,
    sortBy:         filter.sortBy,
    sortDir:        filter.sortDir,
    competitionId:  '',
    minPrice:       debouncedMinPrice,
    maxPrice:       debouncedMaxPrice,
    hideEliminated: filter.hideEliminated,
  }), [debouncedSearch, filter.position, filter.teamCode, filter.sortBy, filter.sortDir, debouncedMinPrice, debouncedMaxPrice, filter.hideEliminated])

  // ── Pagination ──────────────────────────────────────────────────────────────
  // Page lives here (not in PlayerListPanel) so it survives the mobile
  // pitch/list tab switch, which unmounts the panel. Reset to the first page
  // only when the server query actually changes — never on a panel remount.
  const [page, setPage] = useState(0)
  useEffect(() => {
    setPage(0)
  }, [serverFilter])

  // ── Filter actions ────────────────────────────────────────────────────────

  const setSearch = (search: string) =>
    setFilter(prev => ({ ...prev, search }))

  const setPosition = (position: PlayerFilter['position']) =>
    setFilter(prev => ({ ...prev, position }))

  const setTeamCode = (teamCode: PlayerFilter['teamCode']) =>
    setFilter(prev => ({ ...prev, teamCode }))

  const setSortBy = (sortBy: PlayerFilter['sortBy']) =>
    setFilter(prev => ({ ...prev, sortBy, sortDir: prev.sortBy === sortBy
      ? (prev.sortDir === 'asc' ? 'desc' : 'asc')   // toggle direction if same column
      : 'desc'                                        // default desc when switching columns
    }))

  const toggleHideEliminated = () =>
    setFilter(prev => ({ ...prev, hideEliminated: !prev.hideEliminated }))

  // Setting either price handle implies leaving "on budget" mode (manual override).
  const setPriceRange = (minPrice: number | null, maxPrice: number | null) =>
    setFilter(prev => ({ ...prev, minPrice, maxPrice, onBudgetOnly: false }))

  const toggleOnBudget = () =>
    setFilter(prev => ({ ...prev, onBudgetOnly: !prev.onBudgetOnly }))

  const resetFilter = () => setFilter(defaultFilter)

  const toggleAccordion = (playerId: string) =>
    setOpenAccordion(prev => (prev === playerId ? null : playerId))

  return {
    filter,
    serverFilter,
    page,
    setPage,
    openAccordionPlayerId,
    setSearch,
    setPosition,
    setTeamCode,
    setSortBy,
    toggleHideEliminated,
    setPriceRange,
    toggleOnBudget,
    resetFilter,
    toggleAccordion,
  }
}

export type PlayerSearch = ReturnType<typeof usePlayerSearch>
